/**
 * usePlayAnimation - Manages animation timing and loading for play viewers.
 * Handles loading play timing data and managing animation state transitions.
 * Automatically reloads animations when the play changes.
 */

import { useEffect, useRef } from 'react'
import type { Drawing, Player } from '@/types'
import { useAnimation } from '../contexts/AnimationContext'
import { useAnimationTiming } from './UseAnimationTiming'

type AnimatablePlayContent = {
	id: string
	name: string
	players: Player[]
	drawings: Drawing[]
}

type UsePlayAnimationOptions = {
	playContent: AnimatablePlayContent | null
}

/**
 * Hook that manages animation timing and loading for a play.
 * Automatically handles play changes and animation state management.
 *
 * **Relationship with usePlayLoader:**
 * - usePlayAnimation: Higher-level hook for AnimationContext-based animations (combines timing + loading)
 * - usePlayLoader: Low-level loading utility (extracted pattern, more flexible)
 *
 * Use this hook when working with AnimationContext for simplified play loading.
 * For instance-based animations (useAnimationInstance), use usePlayLoader directly.
 *
 * @param playContent - The current play content to animate
 * @returns Animation controls from AnimationContext
 */
export function usePlayAnimation({ playContent }: UsePlayAnimationOptions) {
	const { loadPlay, stop, play } = useAnimation()

	const playTiming = useAnimationTiming(
		playContent
			? {
				id: playContent.id,
				players: playContent.players,
				drawings: playContent.drawings,
			}
			: null,
	)

	const previousPlayIdRef = useRef<string | null>(null)

	useEffect(() => {
		const currentPlayId = playContent?.id ?? null

		if (playTiming && currentPlayId !== previousPlayIdRef.current) {
			previousPlayIdRef.current = currentPlayId
			stop()
			loadPlay(playTiming)
		}
	}, [playTiming, playContent?.id, loadPlay, stop])

	return { play }
}
